import React, { useState, useEffect, useCallback } from "react"
import { Send, Trash2, StickyNote, Loader2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Textarea } from "src/components/ui/textarea"
import { useToast } from "src/components/ui/toast"
import { fetchNotes, createNote, deleteNote } from "./notesApi"

function initials(name = "") {
  return (name.split(" ").map((p) => p[0]).slice(0, 2).join("") || "?").toUpperCase()
}

// Add/list/delete free-text notes for a notable (lead / deal / booking).
//   <NotesPanel notable="deals" id={dealId} />
export function NotesPanel({ notable, id, title = "Notes" }) {
  const { toast } = useToast()
  const [notes, setNotes] = useState(null)
  const [body, setBody] = useState("")
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    if (!id) return
    fetchNotes(notable, id).then(setNotes).catch(() => setNotes([]))
  }, [notable, id])

  useEffect(() => { load() }, [load])

  async function handleAdd() {
    const text = body.trim()
    if (!text) return
    setSaving(true)
    try {
      const note = await createNote(notable, id, text)
      setNotes((cur) => [note, ...(cur || [])])
      setBody("")
    } catch (err) {
      toast({ variant: "destructive", title: "Could not add note", description: err.response?.data?.errors?.[0] })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(noteId) {
    try {
      await deleteNote(notable, id, noteId)
      setNotes((cur) => cur.filter((n) => n.id !== noteId))
    } catch {
      toast({ variant: "destructive", title: "Could not delete note" })
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700">
        <StickyNote className="h-4 w-4 text-slate-400" /> {title}
      </div>

      {/* Composer */}
      <div className="border-b border-slate-100 p-3">
        <Textarea
          rows={2}
          placeholder="Add a note…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleAdd() }}
        />
        <div className="mt-2 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={handleAdd} disabled={saving || !body.trim()}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Add note
          </Button>
        </div>
      </div>

      {/* List */}
      {notes === null ? (
        <div className="px-4 py-6 text-center text-[13px] text-slate-400">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="px-4 py-6 text-center text-[13px] text-slate-400">No notes yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {notes.map((note) => (
            <div key={note.id} className="group flex gap-3 px-4 py-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                {initials(note.author?.full_name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="whitespace-pre-wrap break-words text-sm text-slate-700">{note.body}</div>
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {note.author?.full_name || "Unknown"} · {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
                </div>
              </div>
              <button
                onClick={() => handleDelete(note.id)}
                className="h-6 w-6 shrink-0 rounded text-slate-300 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                title="Delete note"
              >
                <Trash2 className="mx-auto h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
